import React, { useCallback, useEffect, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";

import { useSnackbar } from "notistack";

import TextField from "@/components/TextField";

export interface EditGiftDialogProps {
  caseId: number;
  giftId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  memoriam: string;
  memoriamFrom: string[];
  onChange: (memoriam: string, memoriamFrom: string[]) => void;
}

const EditGiftDialog: React.FC<EditGiftDialogProps> = ({
  caseId,
  giftId,
  open,
  setOpen,
  memoriam: giftMemoriam,
  memoriamFrom: giftMemoriamFrom,
  onChange,
}) => {
  const api = Bananas.useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [memoriam, setMemoriam] = useState<string>(giftMemoriam);
  const [memoriamFromText, setMemoriamFromText] = useState<string>(
    giftMemoriamFrom.join("\n"),
  );

  useEffect(() => {
    setMemoriam(giftMemoriam);
    setMemoriamFromText(giftMemoriamFrom.join("\n"));
  }, [open, giftMemoriam, giftMemoriamFrom]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const memoriamFrom = memoriamFromText.trim().split("\n").filter(Boolean);

      const response = await api.operations["case.case:edit-gift"]?.call({
        params: { case_id: caseId, gift_id: giftId },
        body: {
          memoriam,
          memoriam_from: memoriamFrom,
        },
      });

      if (response?.ok) {
        enqueueSnackbar("Gåvotexten har uppdaterats.", { variant: "success" });
        setOpen(false);
        onChange(memoriam, memoriamFrom);
      } else {
        enqueueSnackbar("Kunde inte uppdatera gåvotexten.", {
          variant: "error",
        });
      }
    },
    [api, enqueueSnackbar, setOpen, caseId, giftId, memoriam, memoriamFromText],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 7,
        },
      }}
      onClose={handleClose}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>Redigera gåva</DialogTitle>

        <DialogContent sx={{ overflow: "visible" }}>
          <Stack direction="column" gap={2}>
            <TextField
              multiline
              id="text"
              label="Gåvotext"
              maxRows={10}
              minRows={2}
              name="text"
              sx={{ borderRadius: 1 }}
              value={memoriam}
              onChange={setMemoriam}
            />
            <TextField
              multiline
              helperText="Ett namn per rad"
              id="from"
              label="Från"
              maxRows={10}
              minRows={2}
              name="from"
              sx={{ borderRadius: 1 }}
              value={memoriamFromText}
              onChange={setMemoriamFromText}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Avbryt</Button>
          <Button type="submit">Spara</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditGiftDialog;
